import { RefObject, useEffect, useState } from 'react';

import isNode from 'detect-node';

interface IScrollState {
  atStart: boolean;
  atEnd: boolean;
  scrollPosition: number;
}

// Sometimes the maths can be slighly off by <1px, so this just adds in a buffer to make sure the
// button atEnd state triggers correctly.
const SCROLL_END_THRESHOLD = 1;

const DEFAULT_SCROLL_STATE: IScrollState = { atStart: true, atEnd: false, scrollPosition: 0 };

function getScrollState(elementRef: RefObject<HTMLDivElement | null> | null): IScrollState {
  if (isNode || !elementRef?.current) {
    return DEFAULT_SCROLL_STATE;
  }

  return {
    scrollPosition: elementRef.current.scrollLeft,
    atStart: elementRef.current.scrollLeft === 0,
    atEnd:
      elementRef.current.scrollLeft + elementRef.current.getBoundingClientRect().width >=
      elementRef.current.scrollWidth - SCROLL_END_THRESHOLD,
  };
}

export function useScrollState(elementRef: RefObject<HTMLDivElement | null> | null): IScrollState {
  const [state, setState] = useState<IScrollState>(DEFAULT_SCROLL_STATE);
  let throttleTimeout: null | NodeJS.Timeout = null;

  const onScrollWithThrottle = (): void => {
    throttleTimeout = null;
    setState(getScrollState(elementRef));
  };

  useEffect(() => {
    const scrollElement = elementRef?.current ?? null;
    const onScroll = (): void => {
      // We throttle to callback to avoid too much jankiness
      // eslint-disable-next-line react-hooks/exhaustive-deps
      throttleTimeout ??= setTimeout(onScrollWithThrottle, 100);
    };

    if (scrollElement) {
      scrollElement.addEventListener('scroll', onScroll);
    }

    return scrollElement
      ? () => {
          scrollElement.removeEventListener('scroll', onScroll);
        }
      : () => {};
  }, [elementRef]);

  return state;
}
